import { subMonths } from 'date-fns';
import { rejectCodes } from '@ourbranch/lookups';

import { defineInternalTask } from '../../../config/types';
import { internalTasks, reviewTeams } from '../../../config/constants';

import { baseBindState } from './lib/helpers';

const TWO_YEARS = 2;

export const homeLowMarketValue = defineInternalTask({
  taskName: internalTasks.HomeLowMarketValue,
  config: {
    staffDisplayName: 'Market value below threshold: verification needed',
    reviewTeam: reviewTeams.uwOps,
    policyTypes: ['H'],
    enabled: true,
    trigger: ({ policy, offer }) => {
      // Per the PR spec, the task fires when EITHER:
      //   (A) state ∉ {MI,MD,PA} AND home >2yrs AND cherre-valuation conditions met, OR
      //   (B) a HOME_LOW_MARKET_VALUE rejection was removed during underwriting.
      // (B) is an independent signal — an UW decision to remove the rejection is an
      // explicit human flag, so it bypasses every guard that only scopes (A), including
      // the offerHome presence check (Path B can fire even when offer.quote.home is null).

      // RemovedRejection.value is the NUMERIC reject code (see
      // `packages/lookups/src/private-lookups/constants.ts:1505` —
      // HOME_LOW_MARKET_VALUE === 50116). Don't compare against the string name.
      const removedRejections = offer?.removedRejections || [];
      const wasLowMarketValueRejectionRemoved = removedRejections.some(
        (r) => r?.value === rejectCodes.HOME_LOW_MARKET_VALUE
      );

      const offerHome = offer?.quote?.home;
      const isExcludedState = ['MI', 'MD', 'PA'].includes(policy.state);
      // Spec: home is ">2 years old". Using `<=` against (currentYear - 2) so a home
      // built in January of (currentYear-2) qualifies — a strict `<` would push the
      // effective cutoff out to 3 years by year arithmetic.
      const minBuiltYear = new Date().getFullYear() - TWO_YEARS;
      const isOldConstruction = !!(offerHome?.yearBuilt && offerHome.yearBuilt <= minBuiltYear);

      let cherreLowMarketValue = false;
      if (offerHome && !isExcludedState && isOldConstruction) {
        const sixMonthsAgo = subMonths(new Date(), 6).getTime();
        const charAttributes = offer?.characteristicsData?.attributes;
        const isOldPurchase = !!(offerHome.purchaseDate && new Date(offerHome.purchaseDate).getTime() < sixMonthsAgo);
        const isRecentValuation = !!(
          offerHome.valuationDate && new Date(offerHome.valuationDate).getTime() > sixMonthsAgo
        );
        // CharacteristicAttribute has BOTH `source` ("selected source") and
        // `dataIndicatedSource` ("algorithm-indicated source"). The legacy
        // `isFromCherre` helper (property-characteristics-reader.ts:116) reads
        // `.source` — use the same field so this check matches the existing
        // UW-rules semantics, not the algorithmic hint.
        const isValuationDataFromCherre = !!(
          charAttributes?.valuationDate?.source === 'cherre' &&
          charAttributes?.estimatedValueAmount?.source === 'cherre'
        );
        const totalRCE = offerHome.replacementCostEstimate?.total;
        const estimatedValueAmount = offerHome.estimatedValueAmount;

        cherreLowMarketValue = !!(
          isOldPurchase &&
          isRecentValuation &&
          isValuationDataFromCherre &&
          estimatedValueAmount &&
          estimatedValueAmount > 0 &&
          totalRCE &&
          estimatedValueAmount * 3 < totalRCE
        );
      }

      if (!cherreLowMarketValue && !wasLowMarketValueRejectionRemoved) {
        return null;
      }

      const addr = offer?.quote?.correctedAddress;
      // Filter empty parts and join — avoids "addr, , ,  zip" style strings when
      // fields are partial (which happens on the rejection-removed path where
      // correctedAddress may be missing city/state).
      const addressParts = addr ? [addr.address, addr.city, addr.state, addr.zip].filter(Boolean) : [];
      return {
        data: {
          address: addressParts.join(', '),
          totalReplacementCostEstimate: offerHome?.replacementCostEstimate?.total ?? undefined,
          estimatedValueAmount: offerHome?.estimatedValueAmount ?? undefined,
          purchaseAmount: offerHome?.purchaseAmount ?? undefined,
          purchaseDate: offerHome?.purchaseDate ?? undefined,
          valuationDate: offerHome?.valuationDate ?? undefined
        },
        bindState: baseBindState(policy)
      };
    },
    description: ({ data }) =>
      `Please check this home to see if it is ineligible for Branch coverage:
Address: ${data.address || 'N/A'}
Home RCE amount: ${data.totalReplacementCostEstimate ?? 'N/A'}
Estimated value amount: ${data.estimatedValueAmount ?? 'N/A'}
Purchase amount: ${data.purchaseAmount ?? 'N/A'}
Purchase date: ${data.purchaseDate ?? 'N/A'}
Valuation date: ${data.valuationDate ?? 'N/A'}`
  }
});
